import { useEffect, useState, useMemo, useRef } from "react";
import { Box, Paper, useTheme, useMediaQuery } from "@mui/material";
import {
    XAxis, YAxis, ResponsiveContainer, Line, Tooltip as RechartsTooltip,
    ReferenceLine, ComposedChart, Scatter
} from "recharts";
import {
    transformDataForChart,
    formatXAxisTick,
    processAlarmPeriods
} from './GraphHelpers/dataTransformUtils';
import {
    renderAlarmBars,
    renderAlarmGradients,
    createCustomAlarmTooltip
} from './GraphHelpers/alarmHelpers';
import { processServiceEvents, groupServiceEventsByTimestamp } from './GraphHelpers/serviceEventsHelpers';
import { createLegendPayload } from './GraphHelpers/legendHelpers';
import { CustomLegend } from './GraphHelpers/CustomLegend';
import { getCurrentEpochDates, getZoomedData } from './GraphHelpers/dateHelpers';
import { useDataFetching } from './GraphHelpers/useDataFetching';
import { ChartInfoDisplay } from './GraphHelpers/ChartInfoDisplay';
import { ZoomControls } from './GraphHelpers/ZoomControls2';
import {
    LoadingDisplay, NoMetricsSelectedDisplay, NoDataAvailableDisplay
} from './GraphHelpers/ChartStateDisplays';
import ServiceFlags from "./ServiceFlags";
import { useChartZoom } from '../../Hooks/useChartZoom';
import { SelectedMetrics, StorageLocation } from "../../Types/AnalyticsGraph";
import { getChartRenderingConfig } from './GraphHelpers/chartRenderingUtils';
import {
    BenchmarkTooltip,
    enhanceChartConfigWithBenchmark,
    transformBenchmarkDataForChart,
    mergeChartDataWithBenchmark,
    hasBenchmarkData
} from '../Benchmark';

interface AnalyticsGraphBaseProps {
    getMetricInfo: (metricKey: string) => { label: string; color: string; unit: string };
    selectedMetrics: { agristarMetrics: string[]; serviceMetrics: string[] };
    data: any;
    serviceData: any;
    loading: boolean;
    error: string | null;
    submitDataFetch: (timeData: object, selectedMetrics: SelectedMetrics, selectedStorage: StorageLocation) => Promise<void>;
    selectedStorage: StorageLocation;
    benchmarkData?: any;
    mode?: 'dark' | 'light';
}

function AnalyticsGraphBase({ getMetricInfo, selectedMetrics, data, serviceData, loading, error,
    submitDataFetch, selectedStorage, benchmarkData, mode = 'dark' }: AnalyticsGraphBaseProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(new Set());

    // Store chart data in state for zoom controls
    const [chartData, setChartData] = useState<any[]>([]);
    const chartContainerRef = useRef<HTMLDivElement>(null!);

    useEffect(() => {
        const regularChartData = transformDataForChart(data);
        let finalChartData = regularChartData;

        // If benchmark data exists, merge it with regular data
        if (benchmarkData && hasBenchmarkData(benchmarkData)) {
            const benchmarkChartData = transformBenchmarkDataForChart(benchmarkData);
            finalChartData = mergeChartDataWithBenchmark(regularChartData, benchmarkChartData);
        }

        setChartData(finalChartData);
    }, [data, benchmarkData]);

    // Get chart rendering configuration and legend, enhanced with benchmark data
    const baseChartRendering = useMemo(() => getChartRenderingConfig(data, getMetricInfo, mode), [data, getMetricInfo, mode]);
    const chartRendering = useMemo(() =>
        enhanceChartConfigWithBenchmark(baseChartRendering, benchmarkData, !!(benchmarkData && hasBenchmarkData(benchmarkData)), getMetricInfo, mode),
        [baseChartRendering, benchmarkData, getMetricInfo, mode]
    );
    const legendPayload = useMemo(() => {
        return createLegendPayload(chartRendering);
    }, [chartRendering]);

    // Zoom logic
    const { zoomStartIndex, zoomEndIndex, handleZoomReset, handleZoomRangeChange, isUserZoom } = useChartZoom({ chartData, chartContainerRef });

    useEffect(() => {
        if (chartData.length > 0) {
            handleZoomRangeChange(0, chartData.length - 1, false);
        }
    }, [chartData, handleZoomRangeChange]);

    // Process data based on zoom
    const zoomedChartData = useMemo(() => getZoomedData(chartData, zoomStartIndex, zoomEndIndex), [chartData, zoomStartIndex, zoomEndIndex]);
    const { startDate: currentEpochStartDate, endDate: currentEpochEndDate } = useMemo(() => getCurrentEpochDates(chartData, zoomStartIndex, zoomEndIndex), [chartData, zoomStartIndex, zoomEndIndex]);

    // Service events processing
    const allServiceEvents = useMemo(() => processServiceEvents(serviceData), [serviceData]);
    const groupedServiceEvents = useMemo(() => groupServiceEventsByTimestamp(allServiceEvents, zoomedChartData), [allServiceEvents, zoomedChartData]);

    // Metric visibility toggle function
    const handleToggleMetric = (metricId: string) => {
        setHiddenMetrics(prev => {
            const newHidden = new Set(prev);
            if (newHidden.has(metricId)) {
                newHidden.delete(metricId);
            } else {
                newHidden.add(metricId);
            }
            return newHidden;
        });
    };

    // Toggle all metrics function
    const handleToggleAllMetrics = (hideAll: boolean) => {
        if (hideAll) {
            // Hide all metrics
            const allMetricIds = new Set(chartRendering.availableMetrics as string[]);
            setHiddenMetrics(allMetricIds);
        } else {
            // Show all metrics
            setHiddenMetrics(new Set());
        }
    };

    // Filter chart rendering based on hidden metrics
    const filteredChartRendering = useMemo(() => {
        if (hiddenMetrics.size === 0) return chartRendering;

        return {
            ...chartRendering,
            chartElementsConfig: {
                ...chartRendering.chartElementsConfig,
                lines: chartRendering.chartElementsConfig.lines.filter((line: any) =>
                    !hiddenMetrics.has(line.key)
                ),
                scatters: chartRendering.chartElementsConfig.scatters.filter((scatter: any) =>
                    !hiddenMetrics.has(scatter.key)
                )
            }
        };
    }, [chartRendering, hiddenMetrics]);

    // Alarm processing
    const alarmPeriods = useMemo(() => processAlarmPeriods(zoomedChartData), [zoomedChartData]);
    const CustomAlarmTooltip = useMemo(() => createCustomAlarmTooltip(alarmPeriods, chartContainerRef), [alarmPeriods]);

    // Data fetching effect
    useDataFetching(isUserZoom, currentEpochStartDate, currentEpochEndDate, zoomStartIndex, zoomEndIndex, selectedMetrics, submitDataFetch, selectedStorage);

    // Determine if we have displayable content
    const hasDisplayableContent = !loading && !error && selectedMetrics.agristarMetrics.length > 0 && chartData.length > 0;
    const hasMetricsSelected = selectedMetrics.agristarMetrics.length > 0;

    // Dynamic height based on content
    const containerHeight = hasDisplayableContent ? '800px' : hasMetricsSelected ? '300px' : '300px';

    return (
        <Paper
            variant="solid"
            sx={{
                p: 0,
                height: containerHeight,
                display: 'flex',
                flexDirection: 'column',
                transition: 'height .3s ease-in-out',
                overflow: 'visible'
            }}
        >

            {loading && <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}><LoadingDisplay /></Box>}
            {!loading && !hasMetricsSelected && <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}><NoMetricsSelectedDisplay /></Box>}
            {!loading && hasMetricsSelected && chartData.length === 0 && <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}><NoDataAvailableDisplay /></Box>}
            {hasDisplayableContent && (
                <Box sx={{
                    flexGrow: 1,
                    width: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <Box sx={{ px: 2 }}>
                        <ChartInfoDisplay
                            chartDataLength={zoomedChartData.length}
                            selectedMetricsLength={selectedMetrics.agristarMetrics.length}
                            scaleGroups={chartRendering.scaleGroups}
                            storageName={selectedStorage.locationName}
                            mode={mode}
                        />
                    </Box>

                    <Box
                        ref={chartContainerRef}
                        sx={{
                            flexGrow: 1,
                            width: '100%',
                            minHeight: { xs: 250, md: 300 },
                            maxHeight: { xs: 400, md: 700 },
                            overflow: 'visible',
                            px: { xs: 0, md: 2 },
                            position: 'relative'
                        }}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart
                                data={zoomedChartData}
                                margin={isMobile 
                                    ? { top: 10, right: 10, left: 0, bottom: 1 } 
                                    : { top: 20, right: 80, left: 80, bottom: 1 }
                                }
                            >
                                {/* Add gradient definitions for sleek alarm bars */}
                                {renderAlarmGradients()}

                                <XAxis
                                    dataKey="timestamp"
                                    type="number"
                                    scale="time"
                                    domain={['dataMin', 'dataMax']}
                                    tickFormatter={formatXAxisTick}
                                    angle={-45}
                                    textAnchor="end"
                                    height={60}
                                    interval="preserveStartEnd"
                                />

                                {/* Render Y axes based on config */}
                                {chartRendering.yAxesConfig.map((axisProps: any) => {
                                    const { key, ...restProps } = axisProps;
                                    return <YAxis key={key} {...restProps} />;
                                })}

                                {/* Custom tooltip that handles regular data, benchmark data, and alarm periods */}
                                <RechartsTooltip
                                    allowEscapeViewBox={{ x: true, y: true }}
                                    wrapperStyle={{ zIndex: 1000 }}
                                    content={(props) => {
                                        const alarmTooltip = CustomAlarmTooltip(props);
                                        if (alarmTooltip) return alarmTooltip;

                                        // Always use BenchmarkTooltip for consistent dark styling
                                        return <BenchmarkTooltip {...props} getMetricInfo={getMetricInfo} />;
                                    }}
                                    cursor={{
                                        stroke: '#6b7280',
                                        strokeWidth: 1,
                                        strokeDasharray: '3 3'
                                    }}
                                />

                                {/* Render lines */}
                                {filteredChartRendering.chartElementsConfig.lines.map((lineProps: any) => {
                                    const { key, ...restProps } = lineProps;
                                    return <Line key={key} {...restProps} />;
                                })}

                                {/* Render scatter points */}
                                {filteredChartRendering.chartElementsConfig.scatters.map((scatterProps: any) => {
                                    const { key, ...restProps } = scatterProps;
                                    return <Scatter key={key} {...restProps} />;
                                })}

                                <YAxis
                                    type="number"
                                    yAxisId="serviceEvents"
                                    domain={['auto', 'auto']}
                                    hide={true}
                                    allowDecimals={false}
                                />

                                {/* Add Service Event Markers */}
                                {Array.from(groupedServiceEvents.entries()).map(([timestamp, events]: any, index: any) => (
                                    <ReferenceLine
                                        key={`service-event-group-${timestamp}-${index}`}
                                        x={timestamp}
                                        stroke="transparent"
                                        yAxisId={"serviceEvents"}
                                        ifOverflow="visible"
                                        label={<ServiceFlags serviceEvents={events} />}
                                    />
                                ))}

                                {/* Render sleek alarm bars using helper function */}
                                {renderAlarmBars(alarmPeriods, chartRendering.yAxesConfig[0]?.yAxisId || "default")}

                            </ComposedChart>
                        </ResponsiveContainer>
                    </Box>
                    {chartData.length > 1 && (
                        <ZoomControls
                            dataLength={chartData.length}
                            startIndex={zoomStartIndex}
                            endIndex={zoomEndIndex}
                            onRangeChange={handleZoomRangeChange}
                            onReset={handleZoomReset}
                        />
                    )}
                </Box>
            )}
            <CustomLegend
                payload={legendPayload}
                maxItemsToShow={isMobile ? 4 : 8}
                onToggleMetric={handleToggleMetric}
                onToggleAllMetrics={handleToggleAllMetrics}
                hiddenMetrics={hiddenMetrics}
            />
        </Paper>
    );
}

export default AnalyticsGraphBase;